/**
 * Electron renderer audio-mute coordination.
 *
 * The DevourPlay Electron main window mutes its ENTIRE webContents at app init
 * (`devourplay-electron/src/main.ts`) so Windows doesn't duck the foreground
 * game's audio the instant the renderer acquires an audio session. That mute is
 * a single global boolean — one switch for the whole window, NOT per element.
 * Every surface that legitimately wants sound (clip playback, embedded streams,
 * challenge/sizzle videos the user unmutes) has to lift it while audible and
 * restore it afterward, or it plays silently in the app while working on web.
 *
 * Because it's one global switch, independent callers toggling it directly
 * collide: a clip modal closing over a still-playing stream would re-mute the
 * stream. So requests are refcounted here — the IPC only sends `false` when the
 * FIRST requester acquires audio and `true` when the LAST one releases. The
 * count lives at module scope, shared across every surface in the renderer.
 *
 * Everything no-ops on plain web (no `window.electronAPI`); the main-process
 * handler is additionally sender-guarded, so a stray call there is harmless.
 */
import { useEffect, useState, type RefObject } from "react";

let activeCount = 0;

function applyRendererMute(muted: boolean): void {
    const electronAPI = (window as unknown as { electronAPI?: { setRendererAudioMuted?: (muted: boolean) => void } }).electronAPI;
    electronAPI?.setRendererAudioMuted?.(muted);
}

/** Register one audio requester. Lifts the renderer mute on the first one. */
export function acquireElectronAudio(): void {
    activeCount += 1;
    if (activeCount === 1) applyRendererMute(false);
}

/** Release one audio requester. Restores the renderer mute when the last leaves. */
export function releaseElectronAudio(): void {
    activeCount = Math.max(0, activeCount - 1);
    if (activeCount === 0) applyRendererMute(true);
}

/**
 * Hold the renderer-audio lift for as long as `active` is true. Pass the media's
 * real audible state — e.g. `!muted && playing` — NOT merely "mounted", so an
 * autoplay-muted sizzle reel doesn't grab the game's audio until the user
 * actually unmutes it. Clip surfaces, where playback is always a deliberate
 * user action, can pass a plain "is a clip loaded/open" boolean.
 */
export function useElectronAudioLift(active: boolean): void {
    useEffect(() => {
        if (!active) return;
        acquireElectronAudio();
        return () => releaseElectronAudio();
    }, [active]);
}

/**
 * Same lift, but driven off a native `<video>`'s real audible state for
 * surfaces that expose only the element's built-in controls (no React mute
 * state to gate on). Subscribes to the element's volume/play events and lifts
 * only while it is unmuted AND actually playing — so an autoplay-muted embed
 * never grabs the game's audio until the user unmutes it via native controls.
 */
export function useElectronAudioForVideo(videoRef: RefObject<HTMLVideoElement | null>): void {
    const [audible, setAudible] = useState(false);
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const update = (): void => setAudible(!video.muted && video.volume > 0 && !video.paused && !video.ended);
        update();
        const events = ["volumechange", "play", "playing", "pause", "ended", "emptied"];
        events.forEach(e => video.addEventListener(e, update));
        return () => events.forEach(e => video.removeEventListener(e, update));
    }, [videoRef]);
    useElectronAudioLift(audible);
}
